import React, { useState } from "react";
import { Button, Col, InputNumber, Row, Select, Space, Table, Typography, message } from "antd";
import { useQuery } from "react-query";
import { fetchProductsIndex } from "../../../api/fulfillment/product";
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";
import { debounce } from "lodash-es";
import { DeleteOutlined, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { useInventoryTransferStore, Product } from "../../../hooks/useInventoryTransferStore";
import * as S from "./styles";

export const SelectProductStep = () => {
  const { data: org } = useOrganizationContext();
  const [search, setSearch] = useState("");
  const { form, products = [], setProducts } = useInventoryTransferStore();

  const isErrorState = () => form?.["products"] === "invalid";

  const productsQuery = useQuery(
    ["products", search],
    () => fetchProductsIndex({ q: String(search).trim(), organization_id: org?.id }),
    {
      select(data) {
        const productLookup = products.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {} as Record<string, Product>);
        return data.data.filter((p) => !productLookup[p.id]).map(({ name, sku, id }) => ({ name, sku, id }));
      },
    }
  );

  const addProduct = (id: string) => {
    const product = productsQuery.data?.find((p) => p.id === id);
    if (!product) return message.error("Product not found");
    setProducts([{ ...product, quantity: 1 }, ...products]);
    setSearch("");
  };

  const removeProduct = (id: string) => {
    setProducts(products.filter((p) => p.id !== id));
  };

  const adjustProductQuantity = (id: string, quantity: number) => {
    setProducts(products.map((p) => ({ ...p, quantity: p.id === id ? quantity : p.quantity })));
  };

  return (
    <S.Container direction="vertical">
      <Typography.Title level={5}>Choose your products</Typography.Title>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Select
            showSearch
            placeholder="Select a product"
            style={{ width: "100%" }}
            value={search}
            onSearch={debounce(setSearch, 500)}
            onSelect={addProduct}
            loading={productsQuery.isLoading}
            suffixIcon={productsQuery.isLoading ? <LoadingOutlined /> : <SearchOutlined />}
            filterOption={false}
            {...(isErrorState() && { status: "error" })}
          >
            {productsQuery.data?.map((o) => (
              <Select.Option key={o.id} value={o.id}>
                <Space direction="vertical" size={0}>
                  <Typography.Text>{o.name}</Typography.Text>
                  <Typography.Text type="secondary">{o.sku}</Typography.Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
          {isErrorState() && <Typography.Text type="danger">Products are required.</Typography.Text>}
        </Col>
        <Col span={24}>
          <Table
            dataSource={products}
            columns={[
              { title: "Product Name", dataIndex: "name" },
              { title: "SKU", dataIndex: "sku" },
              {
                title: "Quantity",
                dataIndex: "quantity",
                render(quantity, data) {
                  return (
                    <InputNumber min={1} value={quantity} onChange={(qty) => adjustProductQuantity(data.id, qty)} />
                  );
                },
              },
              {
                title: "Action",
                dataIndex: "id",
                render(id) {
                  return (
                    <Button icon={<DeleteOutlined />} type="text" size="small" onClick={() => removeProduct(id)} danger>
                      Remove
                    </Button>
                  );
                },
              },
            ]}
            rowKey={(r) => r.id}
            pagination={false}
          />
        </Col>
      </Row>
    </S.Container>
  );
};
